import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { faqData, faqRPData, faqCategories, faqPartnerCategories, faqRPNewData } from './faq-data';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MetaService } from '@app/services/metaservice';
import { Title } from '@angular/platform-browser';
import { ServiceConfigService } from '@app/services/service-config.service';
import { isPartner } from '@app/helpers/mode.helpers';
declare let gtag: Function;

interface FaqCategory {
  id: string;
  title: string;
  icon: string;
  questionKeyList: string[];
}

interface FaqQuestion {
  key: string;
  value: string;
  show: boolean;
}

@Component({
    selector: 'faq',
    templateUrl: './faq.component.html',
    styleUrls: ['./faq.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class FaqComponent implements OnInit {
  private readonly medicalAppealQuestionKeys = ['FAQ_KEY_45', 'FAQ_KEY_46'];
  data = [];
  userType = '';
  awsTranslateSwitch = false;
  faqRevampSwitch = false;
  medicalAppealsSwitch = false;
  searchQuery = '';
  isPartner = false;
  
  categories: FaqCategory[] = [];
  searchResults: FaqQuestion[] = [];
  isSearching = false;
  language;
  isSearchInteraction: boolean = false;

  constructor(
    public activatedRoute: ActivatedRoute, 
    private router: Router, 
    public translateService: TranslateService, 
    private metaService: MetaService, 
    private titleService: Title,
    private serverConfig: ServiceConfigService,
  ) {
    this.titleService.setTitle('FAQ');
  }

  ngOnInit(): void {
    this.awsTranslateSwitch = this.serverConfig.getServerConfig().awsTranslateSwitch;
    this.faqRevampSwitch = this.serverConfig.getServerConfig()?.faqRevampSwitch;
    this.medicalAppealsSwitch = this.serverConfig.getServerConfig()?.medicalAppealsSwitch;
    this.metaService.createCanonicalURL();
    if(sessionStorage.getItem('homeScreen')) {
      sessionStorage.removeItem('homeScreen');
    }
    if(sessionStorage.getItem('userType')) {
      this.userType = sessionStorage.getItem('userType');
    }
    this.setData();
    this.activatedRoute.queryParams.subscribe(res => this.onLoad(res));
    sessionStorage.removeItem('submitSuccess');
  }

  onLoad(response) {
    this.language = response.lang;
    if (response.lang === 'en' || response.lang === 'sp') {
      this.translateService.setDefaultLang(response.lang);
    } else if (response.lang) {
      sessionStorage.setItem('globalTranslatedLan', response.lang);
    }
    if(response.usertype) {
      this.userType = response.usertype;
      this.isPartner = isPartner(this.userType);
      this.setData();
    }
  }

  setData() {
    if (this.userType && this.userType === 'RP') {
      if(this.faqRevampSwitch) {
        this.data = [...faqRPNewData];
      } else {
        this.data = [...faqRPData];
      }
      this.categories = [...faqPartnerCategories];
    } else {
      this.data = JSON.parse(JSON.stringify(faqData));
      this.categories = [...faqCategories];
      // Conditionally set FAQ_KEY_25 value based on awsTranslateSwitch
      const faqItem = this.data[0].questions.find(q => q.key === 'FAQ_KEY_25');
      if (faqItem) {
        faqItem.value = this.awsTranslateSwitch ? 'FAQ_VALUE_25' : 'FAQ_VALUE_25_1';
      }

      if (!this.medicalAppealsSwitch) {
        this.data.forEach(section => {
          section.questions = section.questions.filter(q => !this.medicalAppealQuestionKeys.includes(q.key));
        });
      }
    }
  }

  showHide(num) {
    num.show = !num.show
  }

  cardClickHandler(event: any, item: any) {
    const target = event.target as HTMLElement;
    if(target.tagName.toLowerCase() !== 'a') {
      item.show = !item.show;
    }
  }

  onSearchInput(event: any): void {
    const searchTxt = (event.target.value || '').toLowerCase().trim();
    this.searchQuery = event.target.value;
    
    if (!searchTxt) {
      this.isSearching = false;
      this.searchResults = [];
      return;
    }

    if (!this.isSearchInteraction) {
      gtag('event', 'faq_search_event', {
        'event_category': 'faq_search',
        'event_label': 'input_start',
      });
      this.isSearchInteraction = true;
    }
    
    this.isSearching = true;
    
    let allQuestions: FaqQuestion[] = [];
    this.data.forEach(category => {
      if (category?.questions) {
        allQuestions = [...allQuestions, ...category.questions];
      }
    });
    
    this.searchResults = allQuestions.filter(q => {
      const questionText = this.translateService.instant(q.key).toLowerCase();
      const answerText = this.translateService.instant(q.value).toLowerCase();
      return questionText.includes(searchTxt) || answerText.includes(searchTxt);
    }).map(q => ({
      ...q,
      show: true
    }));
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.isSearching = false;
    this.searchResults = [];
  }

  onCategoryClick(categoryId: string): void {
    if(this.userType !== 'RP') {
      this.router.navigate(['/faq', categoryId], {
        queryParamsHandling: 'preserve'
      });
    } else {
      this.router.navigate(['/faq', categoryId], { 
        queryParams: { usertype: this.userType},
        queryParamsHandling: 'merge'
      });
    } 
  }

  formatFaqAnswer(translatedValue: string): string {
    if (!translatedValue || !this.isArabicOrPersianLang()) {
      return translatedValue;
    }
    return translatedValue.replace(/888-345-5575/g, '5575-345-888');
  }

  isArabicOrPersianLang(): boolean {
    const selectedLang = sessionStorage.getItem('globalTranslatedLan') || sessionStorage.getItem('currentLang') || 'en';
    return selectedLang === 'ar' || selectedLang === 'fa';
  }
  
}